import React, { useCallback, useEffect, useState } from 'react';
import axios from '@/lib/axios';
import { KPI, KPIProvider, useKPI } from '@/Contexts/KpiContext';
import { TemplateProvider, useTemplate } from '@/Contexts/TemplateContext';
import TaskTemplateLibrary from '@/Components/TargetTemplateLibrary';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import { TargetList } from '@/Components/TargetList';
import { useNavigate } from 'react-router-dom';

type NewKPIFormData = {
  name: string;
  description: string;
  period: 'WEEKLY' | 'MONTHLY';
  start_date: string;
  due_date: string;
  assignee: string;
  targets: [];
};

type AssigneeOption = {
  value: string;
  label: string;
};

const CreateKPIView = () => {
  const { fetchKpis, saveKpi } = useKPI();
  const { state: templateState } = useTemplate();
  const [formData, setFormData] = useState<NewKPIFormData>({
    name: '',
    description: '',
    period: 'WEEKLY',
    start_date: '',
    due_date: '',
    targets: [],
    assignee: "",
  });
  const router = useNavigate()
  const [assigneeOptions, setAssigneeOptions] = useState<AssigneeOption[]>([]);
  const [kpi, setKpi] = useState<KPI>({
    id: "new-kpi",
    name: "New KPI",
    description: "",
    period: "WEEKLY",
    targets: [],
    assignee_name: "",
    assignee: "",
  });


  const handleDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;

    const { source, destination } = result;

    if (source.droppableId === 'template-library' && destination.droppableId !== 'template-library') {
      const targetKpiId = destination.droppableId;
      const targetTemplate = templateState.templates?.[source.index];

      if (!targetTemplate) return;

      setKpi(prev => ({
        ...prev,
        targets: [...prev.targets, {
          ...targetTemplate,
          total_expected: 0,
        }]
      }));
    }
  }, [templateState.templates, setKpi]);

  const fetchAssignees = useCallback(async () => {
    try {
      const response = await axios.get('/auth/users/?user_type=MANAGER');
      if (response.status !== 200) throw new Error('Failed to fetch assignees');

      const options = response.data.data.map((user: any) => ({
        value: user.manager_profile,
        label: `${user.first_name} ${user.last_name}`
      }));
      setAssigneeOptions(options);
    } catch (error) {
      console.error('Error fetching assignees:', error);
    }
  }, []);

  const handleCreate = async () => {
    try {
      const response = await axios.post('/tracker/admin/kpis/', {
        ...formData,
        targets: kpi.targets
      });
      if (response.status !== 201) throw new Error('Failed to create KPI');
      router('/kpi_management');
    } catch (error) {
      console.error('Error creating KPI:', error);
    }
  };

  useEffect(() => {
    fetchAssignees();
  }, [fetchAssignees]);

  return (
    <div className="max-w-7xl mx-auto p-6 md:ml-[350px] mb-24">
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* KPI Creation Form */}
          <Card>
            <CardHeader>
              <CardTitle>Create New KPI</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Name</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({
                      ...formData,
                      name: e.target.value
                    })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Description</label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({
                      ...formData,
                      description: e.target.value
                    })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Period</label>
                  <div className="flex gap-2 mt-1">
                    <Button
                      type="button"
                      variant={formData.period === 'WEEKLY' ? 'default' : 'outline'}
                      onClick={() => setFormData({
                        ...formData,
                        period: 'WEEKLY'
                      })}
                    >
                      Weekly
                    </Button>
                    <Button
                      type="button"
                      variant={formData.period === 'MONTHLY' ? 'default' : 'outline'}
                      onClick={() => setFormData({
                        ...formData,
                        period: 'MONTHLY'
                      })}
                    >
                      Monthly
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Assignee</label>
                  <Select
                    value={formData.assignee}
                    onValueChange={(value) => setFormData({
                      ...formData,
                      assignee: value
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue>
                        {assigneeOptions.find(option => option.value === formData.assignee)?.label || 'Select Assignee'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {assigneeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Droppable droppableId={kpi.id}>
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                      <TargetList kpi={kpi} targets={kpi.targets} setKPI={setKpi} />
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
                <div className="pt-4">
                  <Button onClick={handleCreate} className="w-full bg-btn">
                    Create KPI
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Template Library */}
          <TaskTemplateLibrary />
        </div>
      </DragDropContext>
    </div>
  );
};

export default function CreateKPIPage() {
  return (
    <KPIProvider>
      <TemplateProvider>
        <CreateKPIView />
      </TemplateProvider>
    </KPIProvider>
  );
}